import { useState, useEffect, useCallback, useRef } from 'react';
import { CallMetrics, Call } from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';

export const useCalls = () => {
  const [metrics, setMetrics] = useState<CallMetrics | null>(null);
  const [calls, setCalls] = useState<Call[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkPermissionError = (errorMessage?: string) => {
    return errorMessage?.includes('403') || errorMessage?.includes('Forbidden') || false;
  };

  const processCallsResponse = (data: Call[] | { results: Call[]; count: number }) => {
    if (Array.isArray(data)) {
      setCalls(data);
      setTotalCount(data.length);
    } else if ('results' in data) {
      setCalls(data.results || []);
      setTotalCount(data.count || 0);
    } else {
      setCalls([]);
      setTotalCount(0);
    }
  };

  const fetchDashboardMetrics = useCallback(async () => {
    try {
      const response = await ApiClient.get('/telephony/calls/dashboard/');
      const result = await handleApiResult<CallMetrics>(
        response,
        'Failed to fetch dashboard metrics'
      );

      if (result.success && result.data) {
        setMetrics(result.data);
      } else {
        if (checkPermissionError(result.errors?.generalError)) {
          setHasPermissionError(true);
        }
        setMetrics({
          active_calls_count: 0,
          today_calls_count: 0,
          avg_call_duration: 0,
          wallet_balance: 0,
        });
        setError(result.errors?.generalError || 'Failed to fetch dashboard metrics');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch dashboard metrics');
      setMetrics({
        active_calls_count: 0,
        today_calls_count: 0,
        avg_call_duration: 0,
        wallet_balance: 0,
      });
      setError(processedErrors.generalError || 'Failed to fetch dashboard metrics');
    }
  }, []);

  const fetchCallsData = useCallback(
    async (isActive?: boolean, page?: number, pageSize?: number, useMainLoading = false) => {
      try {
        if (useMainLoading) {
          setLoading(true);
        }
        setError(null);

        const params = new URLSearchParams();
        if (isActive !== undefined) {
          params.append('is_active', String(isActive));
        }
        if (page !== undefined) {
          params.append('page', String(page));
        }
        if (pageSize !== undefined) {
          params.append('page_size', String(pageSize));
        }

        const queryString = params.toString();
        const url = `/telephony/calls/${queryString ? `?${queryString}` : ''}`;
        const response = await ApiClient.get(url);
        const result = await handleApiResult<Call[] | { results: Call[]; count: number }>(
          response,
          'Failed to fetch calls'
        );

        if (result.success && result.data) {
          processCallsResponse(result.data);
        } else {
          if (checkPermissionError(result.errors?.generalError)) {
            setHasPermissionError(true);
          }

          setCalls([]);
          setTotalCount(0);
          setError(result.errors?.generalError || 'Failed to fetch calls');
        }
      } catch (err) {
        const processedErrors = handleApiCatch(err, 'Failed to fetch calls');
        setCalls([]);
        setTotalCount(0);
        setError(processedErrors.generalError || 'Failed to fetch calls');
      } finally {
        if (useMainLoading) {
          setLoading(false);
        }
      }
    },
    []
  );

  const fetchCalls = useCallback(
    async (isActive?: boolean, page?: number, pageSize?: number) => {
      return fetchCallsData(isActive, page, pageSize, true);
    },
    [fetchCallsData]
  );

  const fetchCallsForTab = useCallback(
    async (isActive?: boolean, page?: number, pageSize?: number) => {
      return fetchCallsData(isActive, page, pageSize, false);
    },
    [fetchCallsData]
  );

  const fetchCallDetails = useCallback(async (callId: string) => {
    try {
      const response = await ApiClient.get(`/telephony/calls/${callId}/`);
      const result = await handleApiResult<Call>(response, 'Failed to fetch call details');

      if (result.success && result.data) {
        setSelectedCall(result.data);
        return result.data;
      } else {
        setError(result.errors?.generalError || 'Failed to fetch call details');
        return null;
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch call details');
      setError(processedErrors.generalError || 'Failed to fetch call details');
      return null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await ApiClient.get('/telephony/calls/?is_active=true');
        const result = await handleApiResult<Call[] | { results: Call[]; count: number }>(
          response,
          'Failed to fetch active calls'
        );

        if (result.success && result.data) {
          const activeCalls = Array.isArray(result.data) ? result.data : result.data.results || [];

          setCalls(prevCalls => {
            const nonActiveCalls = prevCalls.filter(call => !call.is_active);
            return [...nonActiveCalls, ...activeCalls];
          });
        }
      } catch (err) {
        // Silent fail for polling - don't show errors to user
      }
    }, 10000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const viewCall = async (callId: string) => {
    await fetchCallDetails(callId);
  };

  const getActiveCalls = () => {
    return Array.isArray(calls) ? calls.filter(call => call.is_active) : [];
  };

  const getCallHistory = () => {
    return Array.isArray(calls) ? calls.filter(call => !call.is_active) : [];
  };

  return {
    metrics,
    calls,
    totalCount,
    activeCalls: getActiveCalls(),
    callHistory: getCallHistory(),
    selectedCall,
    loading,
    error,
    hasPermissionError,
    fetchCalls,
    fetchCallsForTab,
    fetchCallDetails,
    viewActiveCall: viewCall,
    viewCallHistory: viewCall,
    startPolling,
    stopPolling,
  };
};
